// Message lists come a page at a time; "Load more" adds the next one. A refresh (new mail, a
// change in another tab, a poll) brings a fresh first page, and these keep the rows the person
// had loaded past it, so the list doesn't shrink back and jump under them.
import type { MessageList } from "../shared/types";

/** The most rows the server sends in one page. */
const MOST_PER_PAGE = 200;

/** The list at `listPath` (a /messages query) from `cursor` on, `limit` rows of it. */
export const pagePath = (listPath: string, cursor: string, limit: number) => {
  const [path, query = ""] = listPath.split("?");
  const params = new URLSearchParams(query);
  params.set("limit", String(limit));
  params.set("cursor", cursor);
  return `${path}?${params}`;
};

/**
 * A fresh first page followed by `more` rows after it (the rows "Load more" had added), fetched
 * with `page`. When those can't be fetched (offline, say), the rows `previous` showed past its
 * first page stand in for them. An abort still throws.
 */
export const keepLoaded = async (
  first: MessageList,
  more: number,
  page: (cursor: string, limit: number) => Promise<MessageList>,
  previous: MessageList | null = null,
): Promise<MessageList> => {
  if (more <= 0 || !first.nextCursor) return first;
  const ids = new Set(first.messages.map((message) => message.id));
  const messages = [...first.messages];
  const add = (rows: MessageList["messages"]) => {
    for (const row of rows) {
      if (ids.has(row.id)) continue;
      ids.add(row.id);
      messages.push(row);
    }
  };
  let cursor: string | null = first.nextCursor;
  let left = more;
  try {
    while (left > 0 && cursor) {
      const next = await page(cursor, Math.min(left, MOST_PER_PAGE));
      add(next.messages);
      left -= next.messages.length;
      cursor = next.nextCursor;
      if (next.messages.length === 0) break;
    }
  } catch (error) {
    if (error instanceof DOMException) throw error;
    if (!previous) return first;
    add(previous.messages.slice(-more));
    return { ...first, messages, nextCursor: previous.nextCursor };
  }
  return { ...first, messages, nextCursor: cursor };
};

/**
 * A "Load more" page came back: whether the list must be fetched again (a fresh first page and
 * every row loaded past it) to take it in. It must while a refresh is still on its way, which
 * counted fewer rows, and when one started after the page was asked for (`refreshes` moved on
 * from `asked`): that one went on from a fresh cursor and the page from the old one, so rows that
 * new mail pushed in between would be missing.
 */
export const refetchForPage = (asked: number, refreshes: number, pending: number) =>
  refreshes !== asked || pending > 0;

/** How long a list fetched or pushed a moment ago still counts as fresh. */
export const FRESH_MS = 2000;

/**
 * When a list was last fetched or pushed, so a change it already covers doesn't fetch it again:
 * in the unified inbox, unread counts that moved with the same change (a write here, a poll, or a
 * live update, which carries the list along with the counts).
 */
export const freshness = (now: () => number = Date.now) => {
  let loading = 0;
  let at = Number.NEGATIVE_INFINITY;
  return {
    /** A fetch of the list starts; the function returned says it is done. */
    start() {
      loading += 1;
      at = now();
      let done = false;
      return () => {
        if (done) return;
        done = true;
        loading -= 1;
        at = now();
      };
    },
    /** The list arrived some other way (the live connection pushed it) at `when`. */
    seen(when: number) {
      at = Math.max(at, when);
    },
    /** A fetch is on its way, or the list came within FRESH_MS. */
    fresh: () => loading > 0 || now() - at < FRESH_MS,
  };
};
